import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, MessageSquare } from 'lucide-react';
import {
  fetchAdminConversationDetail,
  type AdminConversationDetail,
  type AdminMessage,
  type AdminMessageUserRole,
} from '@/lib/messageService';
import { Badge, Button, Loading } from '@/components/ui';

const roleLabels: Record<AdminMessageUserRole, string> = {
  donor: 'ドナー',
  donee: 'ドニー',
  unknown: '不明',
};

const roleVariants: Record<AdminMessageUserRole, 'default' | 'info' | 'success'> = {
  donor: 'info',
  donee: 'success',
  unknown: 'default',
};

function formatDateTime(date?: Date): string {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getInitial(name?: string): string {
  const trimmed = name?.trim();
  return trimmed ? trimmed.slice(0, 1) : '?';
}

function MessageBody({ message, inverted = false }: { message: AdminMessage; inverted?: boolean }) {
  if (message.type === 'image') {
    if (!message.imageUrl) {
      return (
        <p className={`inline-flex items-center gap-2 text-sm ${inverted ? 'text-white/80' : 'text-admin-muted'}`}>
          <ImageIcon className="h-4 w-4" />
          画像URLなし
        </p>
      );
    }

    return (
      <a
        href={message.imageUrl}
        target="_blank"
        rel="noreferrer"
        className={`inline-block overflow-hidden rounded-xl border bg-white ${
          inverted ? 'border-white/50' : 'border-admin-border'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={message.imageUrl}
          alt="送信画像"
          className="max-h-72 max-w-full object-contain"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <p className={`whitespace-pre-wrap text-sm leading-relaxed ${inverted ? 'text-white' : 'text-admin-ink'}`}>
      {message.content}
    </p>
  );
}

interface MessageDetailPanelProps {
  conversationId?: string;
  embedded?: boolean;
  onClose?: () => void;
}

export function MessageDetailPanel({
  conversationId,
  embedded = false,
  onClose,
}: MessageDetailPanelProps) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConversation = useCallback(async () => {
    if (!conversationId) {
      setError('会話IDが不正です');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const nextDetail = await fetchAdminConversationDetail(conversationId);
      if (!nextDetail) {
        setError('会話が見つかりません');
        return;
      }
      setDetail(nextDetail);
    } catch (loadError) {
      console.error('Failed to fetch conversation detail:', loadError);
      setError('メッセージ詳細の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    void loadConversation();
  }, [loadConversation]);

  const participantById = useMemo(() => {
    const map = new Map<string, AdminConversationDetail['conversation']['participantSummaries'][number]>();
    detail?.conversation.participantSummaries.forEach((participant) => {
      map.set(participant.id, participant);
    });
    return map;
  }, [detail]);

  const participantIndexById = useMemo(() => {
    const map = new Map<string, number>();
    detail?.conversation.participantSummaries.forEach((participant, index) => {
      map.set(participant.id, index);
    });
    return map;
  }, [detail]);

  if (isLoading) {
    return <Loading fullScreen={!embedded} message="メッセージ詳細を読み込み中..." />;
  }

  if (error || !detail) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <p className="text-red-700">{error ?? '会話が見つかりません'}</p>
        <Button onClick={embedded ? onClose : () => navigate('/messages')} className="mt-4">
          一覧に戻る
        </Button>
      </div>
    );
  }

  return (
    <section className={embedded ? 'space-y-5 p-5' : 'space-y-6'}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={embedded ? onClose : () => navigate('/messages')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            {embedded ? '閉じる' : '戻る'}
          </Button>
          <div className="mt-4 flex items-center gap-3">
            <div className="rounded-xl bg-admin-accent/10 p-3">
              <MessageSquare className="h-6 w-6 text-admin-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-admin-ink">メッセージ詳細</h1>
              <p className="break-all text-xs text-admin-muted">ID: {detail.conversation.id}</p>
            </div>
          </div>
        </div>
        <Button type="button" variant="secondary" onClick={() => void loadConversation()}>
          再読み込み
        </Button>
      </header>

      <section className="rounded-2xl border border-admin-border bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-admin-ink">参加者</h2>
        <div className="space-y-2">
          {detail.conversation.participantSummaries.map((participant) => (
            <div
              key={participant.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-admin-border bg-admin-surface p-3"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-admin-ink">{participant.displayName}</p>
                  <Badge variant={roleVariants[participant.role]} size="sm">
                    {roleLabels[participant.role]}
                  </Badge>
                </div>
                <p className="mt-1 truncate text-xs text-admin-muted">{participant.email || participant.id}</p>
              </div>
              <Link
                to={`/users/${participant.id}`}
                className="shrink-0 rounded-lg border border-admin-border bg-white px-3 py-1.5 text-xs font-semibold text-admin-ink transition hover:border-admin-accent hover:text-admin-accent"
              >
                ユーザー詳細
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-admin-border bg-white shadow-sm">
        <div className="border-b border-admin-border px-5 py-4">
          <h2 className="text-lg font-bold text-admin-ink">メッセージ</h2>
          <p className="mt-1 text-xs text-admin-muted">
            {detail.messages.length}件 / 最終更新 {formatDateTime(detail.conversation.updatedAt)}
          </p>
        </div>

        {detail.messages.length === 0 ? (
          <div className="p-8 text-center text-sm text-admin-muted">メッセージはありません。</div>
        ) : (
          <div className="space-y-4 bg-admin-bg/40 p-4 md:p-5">
            {detail.messages.map((message) => {
              const sender = participantById.get(message.senderId);
              const senderIndex = participantIndexById.get(message.senderId) ?? 0;
              const alignRight = senderIndex % 2 === 1;
              return (
                <article
                  key={message.id}
                  className={`flex ${alignRight ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex max-w-[86%] gap-2 ${alignRight ? 'flex-row-reverse' : ''}`}>
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        alignRight
                          ? 'bg-admin-accent text-white'
                          : 'border border-admin-border bg-white text-admin-ink'
                      }`}
                    >
                      {getInitial(sender?.displayName ?? message.senderName)}
                    </div>

                    <div className={`space-y-1 ${alignRight ? 'text-right' : 'text-left'}`}>
                      <div className={`flex flex-wrap items-center gap-2 ${alignRight ? 'justify-end' : ''}`}>
                        <p className="text-xs font-semibold text-admin-ink">
                          {sender?.displayName ?? message.senderName}
                        </p>
                        {sender && (
                          <Badge variant={roleVariants[sender.role]} size="sm">
                            {roleLabels[sender.role]}
                          </Badge>
                        )}
                      </div>
                      <div
                        className={`rounded-2xl px-4 py-3 shadow-sm ${
                          alignRight
                            ? 'rounded-tr-sm bg-admin-accent text-white'
                            : 'rounded-tl-sm border border-admin-border bg-white'
                        }`}
                      >
                        <MessageBody message={message} inverted={alignRight} />
                      </div>
                      <div className={`flex flex-wrap gap-2 text-[11px] text-admin-muted ${alignRight ? 'justify-end' : ''}`}>
                        <span>{formatDateTime(message.sentAt)}</span>
                        {message.readAt && (
                          <span>既読: {formatDateTime(message.readAt)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}

export function MessageDetailPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  return <MessageDetailPanel conversationId={conversationId} />;
}
